import { word } from "@modules/core/utils/i18n"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleRights } from "@modules/programBundle/hooks/useProgramBundleRights"
import { useProgramBundleSubscription } from "@modules/programBundle/hooks/useProgramBundleSubscription"
import { HuProgramBundleMiniCard } from "@modules/ui/components/cards/huProgramBundleMiniCard"
import { HuProgramMiniCards } from "@modules/ui/components/cards/huProgramMiniCard"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { useMemo } from "react"

type ProgramBundleInfoProgramsProps = {
  programBundle: ProgramBundleEntity
}

export const ProgramBundleInfoPrograms: React.FC<ProgramBundleInfoProgramsProps> = ({ programBundle }) => {
  const {
    data: programBundleSubscriber,
    isLoading,
    followProgamsInProgramBundle,
    unFollowProgamsInProgramBundle,
  } = useProgramBundleSubscription(programBundle.id)
  const { isAuthor } = useProgramBundleRights(programBundle)

  const canUserFollowBundlePrograms = useMemo(
    () => programBundleSubscriber?.canUserFollowBundlePrograms(),
    [programBundleSubscriber],
  )

  const toggleSelection = () => {
    if (!canUserFollowBundlePrograms) return

    if (programBundle.programs?.length !== programBundle.programs?.filter((p) => p.followedInBundle).length) {
      followProgamsInProgramBundle(programBundle.programs?.map((program) => program.id) || [])
    } else {
      unFollowProgamsInProgramBundle(programBundle.programs?.map((program) => program.id) || [])
    }
  }

  if (isLoading) return <LoadingSpinner />

  return isAuthor ? (
    <div className="flex flex-row flex-wrap gap-2 pt-3">
      {programBundle.programs?.map((program) => (
        <HuProgramMiniCards
          key={program.id}
          item={program}
          className="w-10rem h-8rem mx-1"
          minWidth="8rem"
          editionAllowed
        />
      ))}
    </div>
  ) : (
    <div className="flex flex-column gap-4 my-3">
      {canUserFollowBundlePrograms && (
        <>
          <HUText fontStyle="BS" className="text-center">
            {word("programBundle.follow.description")}
          </HUText>

          <div className="flex flex-row gap-2 justify-content-end align-items-center">
            <HUText fontStyle="BS">{`${programBundle.programs?.filter((p) => p.followedInBundle).length} / ${programBundle.programs?.length} ${word(
              "global.selected",
              {
                count: programBundle.programs?.filter((p) => p.followedInBundle).length,
              },
            )}`}</HUText>
            <HUButton
              type="Default"
              size="XS"
              colorType="Underline"
              className="py-2 px-3"
              onClick={toggleSelection}
              text={
                programBundle.programs?.length !== programBundle.programs?.filter((p) => p.followedInBundle).length
                  ? word("global.filters.selectAll")
                  : word("global.filters.unselectAll")
              }
            />
          </div>
        </>
      )}

      {programBundle.programs?.map((program) => (
        <HuProgramBundleMiniCard
          key={program.id}
          programId={program.id}
          programType={program._programType}
          followProgamsInProgramBundle={followProgamsInProgramBundle}
          unFollowProgamsInProgramBundle={unFollowProgamsInProgramBundle}
          isFollowed={
            programBundleSubscriber?.programBundle?.programs?.find((p) => p.id === program.id)?.followedInBundle
          }
          canUserFollowBundlePrograms={canUserFollowBundlePrograms ?? false}
        />
      ))}
    </div>
  )
}
